/* eslint-disable sonarjs/cognitive-complexity */
import React, { MutableRefObject, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { WebsitePrimaryButton } from '@dev/ee-components';
import { getLandingPageCreatorStore, ModifyLandingPageMetaData, SaveStart, SetIsTemplateOpen, SetLandingPageErrors, StartEditStep, TemplateDeselect } from '@/store/landingPageCreator/actions';
import { LandingPageCreatorStatus, /*, FormErrorNames*/ 
LandingPageErrorNames} from '@/store/landingPageCreator/types';
import { ShowDialogParams } from '@/store/dialog/types';
import { ShowDialog } from '@/store/dialog/actions';
import { getAccountAdvancedOptions } from '@/store/account/actions';
import { useDashboardElementVisibility } from '@/services/hooks/useDashboardElementVisibility';
import { StepTitle } from '../components/StepTitle';
import { StepContent } from '../components/StepContent';
import { Step1Content } from '../steps/Step1Content';
import { Step1EditContent } from '../steps/Step1EditContent';
import { Step2Content } from '../steps/Step2Content';
import { Step2EditContent } from '../steps/Step2EditContent';
import { getStepsValidity } from '../tools/getStepsValidity';
import { getStepsStatuses } from '../tools/getStepsStatuses';
import { Step3Content } from '../steps/Step3Content';
import { Step3EditContent } from '../steps/Step3EditContent';
import { Step4Content } from '../steps/Step4Content';
import { Step4EditContent } from '../steps/Step4EditContent';
import { Step5Content } from '../steps/Step5Content';
import { useLandingPageSubdomain } from './useLandingPageSubdomain';

interface StepsProps {
    onExit: () => void;
    isSavedRef: MutableRefObject<boolean>;
}

export const useSteps = ({ onExit, isSavedRef }: StepsProps): any[] => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const {changeSubdomain,landingPageSubdomain,accountSubdomain} = useLandingPageSubdomain()
    const advancedOptions = useSelector(getAccountAdvancedOptions);
    const { linkInBio: { isLinkInBioInsteadLandingPageVisible } } = useDashboardElementVisibility();

    const { stepInEdit, landingPageObject, landingPagePayload, status, templateObject, errors, isTemplateDeselected, landingPageMetaDataObject, landingPageMetaDataPayload } = useSelector(getLandingPageCreatorStore);
    
    const isSaving = status === LandingPageCreatorStatus.SAVING;

    const isStep3Visible = !advancedOptions?.IsOwnedByReseller;

    const stepsValidity = getStepsValidity(isStep3Visible, landingPageObject, templateObject, landingPageMetaDataObject,landingPageSubdomain);
    const stepsStatuses = getStepsStatuses(stepsValidity, stepInEdit);

    useEffect((): void => {
        isSavedRef.current = !(landingPagePayload || !stepsValidity['5']);
    }, [landingPagePayload,landingPageObject])

    const editTemplate = (): void => {
        dispatch(SetIsTemplateOpen(true));
        dispatch(StartEditStep(2));
    };
    
    const deselectTemplate = (): void => {
        dispatch(StartEditStep(2));
        dispatch(TemplateDeselect()); // this order is important, first step edit then deselect
    };

    const replaceTemplate = (): void => {
        const dialogParams: ShowDialogParams = {
            title: isLinkInBioInsteadLandingPageVisible ? t('replace_linkinbio') : t('replacelandingpage'),
            confirmLabel: t('confirm'),
            body: isLinkInBioInsteadLandingPageVisible ? t('replace_linkinbio_description') : t('replacelandingpadedescription'),
            onClickConfirm: deselectTemplate,
            cancelLabel: t('cancel'),
        };
        dispatch(ShowDialog(dialogParams));
    };

    const setCustomLandingPageDomain = () => {
        const landingUrl = landingPagePayload?.LandingUrl || landingPageObject?.LandingUrl
        //prevent from going further if there is an error 
        if(errors?.landing_invalid_suffix){
            return;
        }

        if(!accountSubdomain) {
            dispatch(SetLandingPageErrors({
                [LandingPageErrorNames.LACK_OF_ACCOUNT_SUBDOMAIN]: true
            }))
            return;
        }

        
        return changeSubdomain({LandingPageID: landingPagePayload?.LandingPageID || landingPageObject?.LandingPageID, UrlSuffix: landingUrl}).then(()=>{
            dispatch(SaveStart({}));
            
        }).catch(({errorKey})=>{
              const errors = {
            ...(errorKey === LandingPageErrorNames.SUBDOMAIN_LANDING_SUBDIRECTORY_IN_USE && {[LandingPageErrorNames.SUBDOMAIN_LANDING_SUBDIRECTORY_IN_USE]: true}),
            ...(errorKey === LandingPageErrorNames.INVALID_SUFFIX && {[LandingPageErrorNames.INVALID_SUFFIX]: true})
        }
       
        dispatch(SetLandingPageErrors(errors))
    })};

    const setEmptyLandingMetaJson = (): void => {
        if (!landingPageObject?.LandingMetaJson && !landingPageMetaDataObject?.data?.socialPreview && !landingPageMetaDataPayload) {
            dispatch(ModifyLandingPageMetaData({ data: { socialPreview: { pageTitle: '', pageDescription: '', imageUrl: '' }} }));
        }
        dispatch(SaveStart({}));
    };

    return [
        {
            title: <StepTitle stepNumber={1} isEditVisible={stepsStatuses['1'] === 'done-open'} isEditDisabled={isSaving} title={isLinkInBioInsteadLandingPageVisible ? t('linkinbio_name') : t('namelandingpage')} />,
            content: (
                <StepContent 
                    isSaveVisible
                    isSaveDisabled={!landingPagePayload?.Name}
                    isEditMode={stepsStatuses['1'] === 'active'}
                    isNew={!stepsValidity['1']}
                    content={<Step1Content />}
                    editContent={<Step1EditContent />}

                />
            ),
            status: stepsStatuses['1'],
            hasErrors: stepInEdit === 1 || !stepsValidity['1'],
        },
        {
            title: <StepTitle 
                stepNumber={2} 
                isEditVisible={stepsStatuses['2'] === 'done-open'} 
                isEditDisabled={isSaving} 
                title={isLinkInBioInsteadLandingPageVisible ? t('designlinkinbio') : t('designlandingpage')}
                customButtonTitle1={t('replace')}
                customButtonTitle2={t('edittemplate')}
                customButtonAction1={replaceTemplate}
                customButtonAction2={editTemplate}
                skipEditStepWarning1
            />,
            content: (
                <StepContent
                    isSaveVisible={false}
                    isSaveDisabled={!templateObject?.EditorMetaData}  
                    isEditMode={stepsStatuses['2'] === 'active'}
                    isNew={!stepsValidity['2']}
                    content={<Step2Content isEditMode={false}/>}
                    editContent={!isTemplateDeselected && templateObject?.EditorMetaData ? <Step2Content isEditMode /> : <Step2EditContent /> }
                />
            ),
            status: stepsStatuses['2'],
            hasErrors: stepInEdit === 2 || !stepsValidity['2'],
        },
        ...(isStep3Visible ? [{
            title: <StepTitle stepNumber={3} isEditVisible={stepsStatuses['3'] === 'done-open'} isEditDisabled={isSaving} title={isLinkInBioInsteadLandingPageVisible ? t('linkinbiolink') : t('landingpagelink')} />,
            content: (
                <StepContent 
                    isSaveVisible
                    isSaveDisabled={errors?.landing_subdirectory_in_use || errors?.landing_invalid_suffix}  
                    isEditMode={stepsStatuses['3'] === 'active'}
                    isNew={!stepsValidity['3']}
                    content={<Step3Content />} 
                    editContent={<Step3EditContent />}
                    beforeSave
                    onSave={setCustomLandingPageDomain}
                />
            ),
            status: stepsStatuses['3'],
            hasErrors: stepInEdit === 3 || !stepsValidity['3'],
        }] : []),
        {
            title: <StepTitle stepNumber={4} isEditVisible={stepsStatuses['4'] === 'done-open'} isEditDisabled={isSaving} title={t('lp_creator_social_header')} />,
            content: (
                <StepContent 
                    isSaveVisible
                    isSaveDisabled={false}  
                    isEditMode={stepsStatuses['4'] === 'active'}
                    isNew={!stepsValidity['4']}
                    content={<Step4Content />} 
                    editContent={<Step4EditContent />}
                    onSave={setEmptyLandingMetaJson}
                />
            ),
            status: stepsStatuses['4'],
            hasErrors: stepInEdit === 4 || !stepsValidity['4'],
        },
        {
            title: t('publish'),
            content: <Step5Content isStepActive={stepsStatuses['5'] === 'active'}/>,
            status: stepsStatuses['5'],
        },
        {
            title: '',
            content: (
                <WebsitePrimaryButton text={isLinkInBioInsteadLandingPageVisible ? t('backtolinkinbiopages') : t('backtolandingpages')} onClick={onExit} />
            ),
            status: 'content-only',
            hasErrors: false,
        },
    ];
};
